import { comparer, reaction } from "mobx";
import { useEffect, useRef } from "react";

import { useInputVariables } from "@/components/Playground/hooks/useInputVariables";
import { usePlaceholderContainer } from "@/components/Playground/Messages/PlaceholderContainer/context/placeholder-container-context";
import { ChildMessagesStore } from "@/components/Playground/Messages/PlaceholderContainer/stores/child-messages-store";
import { validateMessagesJson } from "@/components/Playground/Messages/PlaceholderContainer/errors";
import type { Message, MessageType } from "@/components/Playground/types";
import type { Content, FunctionCall, ToolCall } from "@/types";
import type { Variable } from "@/components/InputVariableConfigurator/types";
import { usePlayground } from "@/components/Playground/playground-store";

/**
 * Serializes a childStore's messages to JSON string.
 * Produces backend-compatible format matching the MessageStore schema getter.
 */
function serializeChildStore(childStore: ChildMessagesStore): string {
  const data = childStore.messages.map(msg => {
    const message: Message = {
      id: msg.id,
      role: msg.role,
      content: msg.rawContent.length > 0 ? msg.rawContent : [],
    };

    if (msg.tool_calls && msg.tool_calls.length > 0) {
      message.tool_calls = msg.tool_calls;
    }

    if (msg.function_call) {
      message.function_call = msg.function_call;
      message.function_call.name = msg.function_call.name || "";
    }

    if (msg.role === "tool" && msg.name) {
      message.tool_call_id = msg.name || "";
    } else if (msg.name) {
      message.name = msg.name;
    }

    return message;
  });
  return JSON.stringify(data);
}

/**
 * Deserializes a JSON string into Message objects compatible with MessageStore constructor.
 * Handles role-specific fields: tool_calls, function_call, tool_call_id, name.
 * Returns null if parsing fails or data is invalid.
 */
function parseMessagesFromJson(value: string): Message[] | null {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return null;
    }

    const valid = parsed.every(
      item => typeof item === "object" && item !== null && "role" in item && "content" in item,
    );

    if (!valid) {
      return null;
    }

    return (parsed as Record<string, unknown>[]).map(item => {
      const message: Message = {
        id: item.id as string,
        role: item.role as MessageType,
        content: item.content as Content[],
      };

      if (Array.isArray(item.tool_calls) && item.tool_calls.length > 0) {
        message.tool_calls = item.tool_calls as ToolCall[];
      }

      if (item.function_call && typeof item.function_call === "object") {
        message.function_call = item.function_call as FunctionCall;
      }

      if (typeof item.tool_call_id === "string") {
        message.tool_call_id = item.tool_call_id;
      }

      if (typeof item.name === "string") {
        message.name = item.name;
      }

      return message;
    });
  } catch {
    return null;
  }
}

/**
 * Hook that synchronizes PlaceholderContainer with input variables.
 *
 * For multi-set support:
 * - Each variable set has its own childStore
 * - Hydrates all childStores from their respective set's variable value
 * - Syncs each childStore's changes to its respective set's variable value
 * - Handles name changes (add, rename, remove) across all sets
 * - Cleans up variables on unmount
 */
export const useBiSyncPlaceholder = () => {
  const playground = usePlayground();

  const {
    ensureVariable,
    removeVariable,
    renameVariable,
    getVariableValueForSet,
    updateVariableValueForSet,
  } = useInputVariables();

  const { message, childStoresMap, variableSets } = usePlaceholderContainer();
  const isHydrating = useRef<Set<string>>(new Set()); // Track hydrating state per set
  const previousVariable = useRef<Partial<Variable> | null>(null);

  // Get the placeholder variable name (from message.name)
  const variableName = message.name || "";
  const isValidName = variableName.length > 0;

  // 1. Handle variable name changes (add, rename, or remove based on validity)
  useEffect(() => {
    const previousKey = previousVariable.current?.key;
    const hadValidName = previousKey !== undefined && previousKey.length > 0;
    const isRename = isValidName && hadValidName && previousKey !== variableName;

    // Ensure variable exists when valid, EXCEPT during rename (which preserves value differently).
    // Variable values survive StrictMode/DnD remounts because removeVariable is only called
    // when the message is actually deleted — Effect #3 hydrates stores from preserved values.
    if (isValidName && !isRename) {
      ensureVariable(variableName);
    }

    // Early exit if name hasn't changed (after ensuring variable exists)
    if (variableName === previousKey) {
      return;
    }

    // Handle name transitions
    if (isRename) {
      // Rename: old valid → new valid (preserves value)
      renameVariable(previousKey!, variableName);
    } else if (!isValidName && hadValidName) {
      // Remove: valid name → invalid/empty name
      removeVariable(previousKey!);
    }

    previousVariable.current = { key: variableName, value: "" };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableName, isValidName]);

  // 2. Cleanup on unmount - remove variable only when placeholder is actually deleted
  useEffect(
    () => () => {
      const previousKey = previousVariable.current?.key;
      if (previousKey && previousKey.length > 0) {
        // Only remove variable if the message was deleted from the playground.
        // During DnD reorder the message still exists in the array — skip removal
        // to preserve child message data for the remounted component.
        const messageStillExists = playground.messages.some(m => m.id === message.id);
        if (!messageStillExists) {
          removeVariable(previousKey);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // 3. Hydrate ALL childStores from their respective variable set values on mount/name change
  useEffect(() => {
    if (!isValidName) {
      return;
    }

    variableSets.forEach(set => {
      const store = childStoresMap.get(set.id);
      if (!store) {
        return;
      }

      const existingValue = getVariableValueForSet(set.id, variableName);
      if (existingValue) {
        const parsed = parseMessagesFromJson(existingValue);
        if (parsed && parsed.length > 0) {
          isHydrating.current.add(set.id);
          store.hydrateFromMessages(parsed);
          isHydrating.current.delete(set.id);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableName, isValidName, variableSets.length]);

  // 4. Sync each childStore's changes to its respective variable set value
  // Also clears validation errors for messages that become valid after UI edit
  useEffect(() => {
    if (!isValidName) {
      return;
    }

    const disposers: (() => void)[] = [];

    variableSets.forEach(set => {
      const store = childStoresMap.get(set.id);
      if (!store) {
        return;
      }

      const dispose = reaction(
        () => serializeChildStore(store),
        jsonValue => {
          // Skip if we're currently hydrating this store
          if (isHydrating.current.has(set.id)) {
            return;
          }

          updateVariableValueForSet(set.id, variableName, jsonValue);

          // UI edits always produce valid structure — validate and clear errors
          // for messages that are now valid (keep errors for still-invalid messages)
          const validationResult = validateMessagesJson(jsonValue);
          if (validationResult.success) {
            // All valid — clear everything
            store.clearAllErrors();
          } else {
            // Clear parse error (UI always produces valid JSON)
            store.setParseError(null);
            // Update validation errors to only include still-invalid messages
            store.setValidationErrors(validationResult.messageErrors);
          }
        },
        { delay: 100, fireImmediately: false },
      );

      disposers.push(dispose);
    });

    return () => disposers.forEach(d => d());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableName, isValidName, variableSets.length]);

  // 5. Watch for input variable value changes (external edits) - single reaction for all sets.
  // Validates external JSON and sets errors before hydrating.
  useEffect(() => {
    if (!isValidName) {
      return;
    }

    const dispose = reaction(
      () =>
        playground.input_variable_sets
          .filter(set => set.variables.some(v => v.key === variableName))
          .map(set => ({
            setId: set.id,
            value: (set.variables.find(v => v.key === variableName)?.value as string) ?? null,
          })),
      entries => {
        for (const { setId, value } of entries) {
          if (isHydrating.current.has(setId) || !value) {
            continue;
          }

          const store = childStoresMap.get(setId);
          if (!store) {
            continue;
          }

          const currentJson = serializeChildStore(store);

          // Skip if variable value already matches store (Effect #4 echo)
          if (value === currentJson) {
            continue;
          }

          // External change — validate first
          const validationResult = validateMessagesJson(value);

          if (validationResult.parseError) {
            // Level 1 error: can't parse at all — set parse error, don't hydrate
            store.setParseError(validationResult.parseError);
            store.setValidationErrors(new Map());
            continue;
          }

          // Clear parse error, set any structural errors
          store.setParseError(null);
          store.setValidationErrors(validationResult.messageErrors);

          // Hydrate with parsed data (even if has structural errors — render what we can)
          const parsed = parseMessagesFromJson(value);
          if (parsed) {
            isHydrating.current.add(setId);
            store.hydrateFromMessages(parsed);
            isHydrating.current.delete(setId);
          }
        }
      },
      { fireImmediately: false, equals: comparer.structural },
    );

    return () => dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableName, isValidName]);

  // 6. Watch if input variable's name changed externally and update placeholder name
  useEffect(() => {
    const dispose = reaction(
      () =>
        playground.input_variable_sets.flatMap(set =>
          set.variables.map(v => ({ setId: set.id, key: v.key })),
        ),
      (keyNames, oldKeyNames) => {
        if (oldKeyNames.length !== keyNames.length) {
          return;
        }

        const updatedName = keyNames.find(
          (inputVariable, index) =>
            oldKeyNames[index].key === message?.name &&
            inputVariable.key !== oldKeyNames[index].key,
        )?.key;

        if (typeof updatedName === "string") {
          message.setMessage(updatedName);
        }
      },
      { fireImmediately: false },
    );
    return dispose;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message?.name]);
};
